import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAllProfiles,
  selectAllUsers,
  updateProfileAction,
  updateUserAction,
} from '../../application/store/reducers/users';
import useOrgStore from '../../application/store/zustand/useOrgStore';
import {
  addSeat,
  getProrationPreview,
  getSeatInfo,
  SeatInfo,
} from '../../application/routes/settings/components/PaymentMethods/paymentApi';
import { setUserStatus } from '../repositories/users.repository';
import { useToast } from '../components/ui/toast';

type Action = 'enable' | 'disable';
type Target = { id: string; username: string; name?: string };

interface Prompt {
  title: string;
  description: string;
  confirmLabel: string;
  variant: 'danger' | 'warning' | 'info';
  purchaseSeat: boolean;
}

interface Pending extends Prompt {
  user: Target;
  action: Action;
  onDone?: (status: string) => void;
}

// Deleted users have no Cognito login left to change.
export const canChangeStatus = (status?: string) =>
  !!status && status !== 'deleted';

// Activating uses a seat in seat_based orgs (seats = purchased - active
// users), so mirror "New person": say how many are left, or offer to buy one.
export function activatePrompt(
  name: string,
  seats?: SeatInfo,
  price?: string | null
): Prompt {
  const base = {
    title: `Activate ${name}?`,
    confirmLabel: 'Activate',
    variant: 'info' as const,
    purchaseSeat: false,
  };
  if (seats && seats.availableSeats > 0) {
    const n = seats.availableSeats;
    return {
      ...base,
      description: `You have ${n} seat${
        n === 1 ? '' : 's'
      } remaining. Activating ${name} will use 1 seat.`,
    };
  }
  if (seats && seats.customerType === 'seat_based') {
    return {
      title: 'No Seats Available',
      description: `You've used all your seats. To activate ${name}, you'll need to ${
        price ? `purchase a seat for ${price}` : 'purchase an additional seat'
      }. You'll be charged when you continue.`,
      confirmLabel: 'Purchase and activate',
      variant: 'warning',
      purchaseSeat: true,
    };
  }
  return { ...base, description: `${name} will be able to sign in again.` };
}

// The People list and profile read users from the v1 redux slice, so the new
// status is written there.
export function useUserStatus() {
  const dispatch = useDispatch();
  const users = useSelector(selectAllUsers);
  const profiles = useSelector(selectAllProfiles);
  const toast = useToast();
  const [pending, setPending] = useState<Pending | null>(null);
  const [checkingSeats, setCheckingSeats] = useState(false);
  const orgID = (global as any).me?.orgID;

  const mutation = useMutation({
    mutationFn: async ({ user, action, purchaseSeat }: Pending) => {
      if (purchaseSeat) {
        const res = await addSeat(orgID, 1);
        if (!res?.success) {
          const err = new Error(
            res?.error || 'Failed to purchase seat. Please try again.'
          );
          err.name = 'SeatPurchaseError';
          throw err;
        }
      }
      await setUserStatus(user, action);
    },
    onSuccess: (_, { user, action, onDone }) => {
      const status = action === 'enable' ? 'active' : 'disabled';
      if (users[user.id]) {
        dispatch(updateUserAction({ ...users[user.id], status }));
      }
      const profile = profiles[user.id];
      if (profile) {
        dispatch(
          updateProfileAction({ ...profile, user: { ...profile.user, status } })
        );
      }
      onDone?.(status);
      toast.success(
        `${user.name || 'User'} ${
          action === 'enable' ? 'activated' : 'deactivated'
        }`
      );
    },
    // Amplify rejects GraphQL failures with a plain {data, errors} object,
    // so only the seat purchase error carries a message worth showing.
    onError: (err: any) => {
      console.error('Error changing user status:', err);
      toast.error(
        err?.name === 'SeatPurchaseError'
          ? err.message
          : 'There was a problem updating the status. Please try again.'
      );
    },
    onSettled: () => setPending(null),
  });

  const request = async (
    user: Target,
    action: Action,
    onDone?: (status: string) => void
  ) => {
    const name = user.name || 'this user';
    if (action === 'disable') {
      setPending({
        user,
        action,
        onDone,
        title: `Deactivate ${name}?`,
        description: `${name} won't be able to sign in until they're activated again.`,
        confirmLabel: 'Deactivate',
        variant: 'danger',
        purchaseSeat: false,
      });
      return;
    }

    let seats: SeatInfo | undefined;
    let price: string | null = null;
    if (useOrgStore.getState()?.orgData?.billingType === 'seat_based') {
      setCheckingSeats(true);
      try {
        const res = await getSeatInfo(orgID);
        seats = res?.success ? res.data : undefined;
        if (seats && seats.availableSeats <= 0) {
          const preview = await getProrationPreview(orgID, 1);
          price = preview?.success
            ? preview.data?.prorationAmountFormatted || null
            : null;
        }
      } catch (err) {
        // ponytail: same as "New person", a failed seat check doesn't block.
        console.error('Seat check failed, proceeding', err);
      } finally {
        setCheckingSeats(false);
      }
    }
    setPending({ user, action, onDone, ...activatePrompt(name, seats, price) });
  };

  return {
    request,
    isBusy: checkingSeats || mutation.isPending,
    confirmProps: {
      open: !!pending,
      onClose: () => !mutation.isPending && setPending(null),
      onConfirm: () => {
        if (pending) {
          mutation.mutate(pending);
        }
      },
      title: pending?.title || '',
      description: pending?.description || '',
      confirmLabel: pending?.confirmLabel,
      cancelLabel: 'Cancel',
      variant: pending?.variant,
      isLoading: mutation.isPending,
    },
  };
}
